import { skipToken } from '@reduxjs/toolkit/query/react'
import { useMemo } from 'react'
import { useFetchInfluencerAnalyzeQuery } from '@/api/influencer-api'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchKolAnalyzeQuery } from '@/api/workspace-api/kol-api'
import { KOLAnalyze } from '@/api/workspaces-api'
import { usePermissions } from '@/hooks/use-permissions'
import { Optional } from '@/types/optional'
import { PlatformCode } from '@/utils/convert-platform'

type UseKolAnalyze = (params: {
  kolId?: string
  kolDcIdHashed?: string
  isKolInfoLocked: boolean
  platform: PlatformCode
}) => {
  analyze?: KOLAnalyze[]
  isFetching: boolean
}

const useKolAnalyze: UseKolAnalyze = ({
  kolId,
  kolDcIdHashed,
  isKolInfoLocked,
  platform,
}) => {
  const { withKolPermission } = usePermissions()
  const { data: userStatus, isFetching: isFetchingUserStatus } =
    useFetchUserStatusQuery(withKolPermission ? skipToken : undefined)
  const { data: analyzeByAd, isFetching: isFetchingKOLAnalyzeByAd } =
    useFetchKolAnalyzeQuery(
      !withKolPermission &&
        kolId &&
        userStatus?.currentWorkspaceId &&
        !isKolInfoLocked
        ? {
            workspaceId: userStatus.currentWorkspaceId,
            radar_uuid: kolId,
            dc_id_hashed: kolDcIdHashed,
          }
        : skipToken,
    )
  const { data: analyzeByKol, isFetching: isFetchingKOLAnalyzeByKol } =
    useFetchInfluencerAnalyzeQuery(
      withKolPermission && kolId ? kolId : skipToken,
    )

  const analyze = useMemo((): Optional<KOLAnalyze[]> => {
    const response = withKolPermission ? analyzeByKol : analyzeByAd
    return response?.data[platform]
  }, [analyzeByAd, analyzeByKol, platform, withKolPermission])

  return {
    analyze,
    isFetching: withKolPermission
      ? isFetchingKOLAnalyzeByKol
      : isFetchingUserStatus || isFetchingKOLAnalyzeByAd,
  }
}

export default useKolAnalyze
